/**
 * Shared helpers of the structure command bodies (W1-10a core-commands-structure; ARCHITECTURE.md §5.4).
 *
 * Everything here works on a TxBuilder: record lookups that fail with a translatable reason when the record is gone
 * (stale selections), track and key utilities that keep the model invariants (a track exists iff it has keys, holds
 * arrive as jumps, the lead-in rule), removal of a line's content, a model-level evaluator for "keep it where it is"
 * conversions (DESIGN.md §6.2 in f64; the exact f32 renderer lives in the engine), link-group ids and asset helpers.
 * Failure reasons and notes are catalogue keys of namespace 'cmd' (structure.messages.ts).
 */

import { Ease as EaseApi, LINEAR } from '../ease/index';
import type { Ease } from '../ease/types';
import { PROP_SPECS } from '../model/props';
import type {
  AssetId,
  AssetRef,
  BpmId,
  BpmPoint,
  Effect,
  EffectId,
  GroupId,
  Key,
  KeyId,
  Line,
  LineGroup,
  LineId,
  LineProp,
  Marker,
  MarkerId,
  Note,
  NoteId,
  Track,
  TrackId,
  TrackOwner,
  TrackValue,
  TrackValueType,
  Video,
  VideoId,
} from '../model/types';
import type { TxBuilder } from '../ops/types';
import { Beats } from '../time/beat';
import { formatBeat } from '../time/beatSyntax';
import type { Beat, Sec } from '../time/types';

export type Tx = TxBuilder;

/** A record patch under construction (fields assigned one by one before `tx.update`). */
export type Patch<R> = { -readonly [K in keyof R]?: R[K] | undefined };

// ---------------------------------------------------------------------------------------------------------------
// Record lookups (fail with a reason when a record is gone)
// ---------------------------------------------------------------------------------------------------------------

export function lineOf(tx: Tx, id: LineId): Line {
  const l = tx.doc.lines.get(id);
  if (l === undefined) tx.fail({ key: 'cmd.missing.line', params: { id } });
  return l;
}

export function noteOf(tx: Tx, id: NoteId): Note {
  const n = tx.doc.notes.get(id);
  if (n === undefined) tx.fail({ key: 'cmd.missing.note', params: { id } });
  return n;
}

export function keyOf(tx: Tx, id: KeyId): Key {
  const k = tx.doc.keys.get(id);
  if (k === undefined) tx.fail({ key: 'cmd.missing.key', params: { id } });
  return k;
}

export function trackOf(tx: Tx, id: TrackId): Track {
  const t = tx.doc.tracks.get(id);
  if (t === undefined) tx.fail({ key: 'cmd.missing.track', params: { id } });
  return t;
}

export function groupOf(tx: Tx, id: GroupId): LineGroup {
  const g = tx.doc.groups.get(id);
  if (g === undefined) tx.fail({ key: 'cmd.missing.group', params: { id } });
  return g;
}

export function markerOf(tx: Tx, id: MarkerId): Marker {
  const m = tx.doc.markers.get(id);
  if (m === undefined) tx.fail({ key: 'cmd.missing.marker', params: { id } });
  return m;
}

export function bpmOf(tx: Tx, id: BpmId): BpmPoint {
  const p = tx.doc.bpm.get(id);
  if (p === undefined) tx.fail({ key: 'cmd.missing.bpm', params: { id } });
  return p;
}

export function effectOf(tx: Tx, id: EffectId): Effect {
  const e = tx.doc.effects.get(id);
  if (e === undefined) tx.fail({ key: 'cmd.missing.effect', params: { id } });
  return e;
}

export function videoOf(tx: Tx, id: VideoId): Video {
  const v = tx.doc.videos.get(id);
  if (v === undefined) tx.fail({ key: 'cmd.missing.video', params: { id } });
  return v;
}

export function assetOf(tx: Tx, id: AssetId): AssetRef {
  const a = tx.doc.assets.get(id);
  if (a === undefined) tx.fail({ key: 'cmd.missing.asset', params: { id } });
  return a;
}

/** Existing records of `ids` in input order, duplicates and deleted ids dropped (stale selections are harmless). */
export function existingNotes(tx: Tx, ids: readonly NoteId[]): Note[] {
  const seen = new Set<NoteId>();
  const out: Note[] = [];
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    const n = tx.doc.notes.get(id);
    if (n !== undefined) out.push(n);
  }
  return out;
}

/** Existing line ids of `ids`, deduplicated, in input order. */
export function existingLines(tx: Tx, ids: readonly LineId[]): LineId[] {
  const seen = new Set<LineId>();
  const out: LineId[] = [];
  for (const id of ids) {
    if (seen.has(id) || !tx.doc.lines.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------------------------------------------

export function isFiniteNumber(x: unknown): x is number {
  return typeof x === 'number' && Number.isFinite(x);
}

/** -0 becomes 0 (JSON cannot carry -0 and snapshots compare exactly). */
export function noNegZero(x: number): number {
  return x === 0 ? 0 : x;
}

export function isU8Triple(v: unknown): boolean {
  return Array.isArray(v) && v.length === 3 && v.every((c) => Number.isInteger(c) && c >= 0 && c <= 255);
}

export const I32_MIN = -2147483648;
export const I32_MAX = 2147483647;

export function isI32(x: number): boolean {
  return Number.isInteger(x) && x >= I32_MIN && x <= I32_MAX;
}

// ---------------------------------------------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------------------------------------------

/** Human-facing name of a line for messages: its name, or its export index when unnamed. */
export function lineLabel(tx: Tx, line: Line): string {
  return line.name.length > 0 ? line.name : `#${String(tx.idx.lineIndex(line.id))}`;
}

// ---------------------------------------------------------------------------------------------------------------
// Tracks and keys
// ---------------------------------------------------------------------------------------------------------------

/** Keys of a track in beat order. */
export function keysOf(tx: Tx, track: TrackId): Key[] {
  const out: Key[] = [];
  for (const id of tx.idx.keysByTrack(track)) {
    const k = tx.doc.keys.get(id);
    if (k !== undefined) out.push(k);
  }
  return out;
}

/** Every track of a line property (all layers), ordered by layer. */
export function tracksOfProp(tx: Tx, line: LineId, prop: LineProp): TrackId[] {
  return [...(tx.idx.tracksOfLine(line).get(prop) ?? [])];
}

/** All tracks of a line. */
export function tracksOfLine(tx: Tx, line: LineId): TrackId[] {
  const out: TrackId[] = [];
  for (const ids of tx.idx.tracksOfLine(line).values()) out.push(...ids);
  return out;
}

export function hasTrack(tx: Tx, line: LineId, prop: LineProp): boolean {
  return tracksOfProp(tx, line, prop).length > 0;
}

/** Removes a track and all its keys. */
export function deleteTrack(tx: Tx, track: TrackId): void {
  for (const k of [...tx.idx.keysByTrack(track)]) tx.remove('keys', k);
  tx.remove('tracks', track);
}

/** Removes every track of a line property (all layers). Returns how many keys were removed. */
export function deletePropTracks(tx: Tx, line: LineId, prop: LineProp): number {
  let keys = 0;
  for (const t of tracksOfProp(tx, line, prop)) {
    keys += tx.idx.keysByTrack(t).length;
    deleteTrack(tx, t);
  }
  return keys;
}

export interface KeyDraft {
  readonly t: Beat;
  readonly vIn: TrackValue;
  readonly vOut: TrackValue;
  readonly ease: Ease;
  readonly link?: number;
  readonly src?: Key['src'];
}

/** Creates a track with keys (callers guarantee the key invariants of the drafts). */
export function createTrack(
  tx: Tx,
  owner: TrackOwner,
  valueType: TrackValueType,
  keys: readonly KeyDraft[],
  importedLeadIn = false,
): TrackId {
  const id = tx.newId('tracks');
  tx.insert('tracks', { id, owner, valueType, ...(importedLeadIn ? { importedLeadIn: true as const } : {}) });
  for (const k of keys) {
    const kid = tx.newId('keys');
    tx.insert('keys', {
      id: kid,
      track: id,
      t: k.t,
      vIn: k.vIn,
      vOut: k.vOut,
      ease: k.ease,
      link: k.link ?? 0,
      ...(k.src !== undefined ? { src: k.src } : {}),
    });
  }
  return id;
}

/** A line track owner. */
export function lineOwner(line: LineId, prop: LineProp, layer = 0): TrackOwner {
  return { kind: 'line', line, prop, layer };
}

/**
 * Makes the (line, prop, layer) track a single key at beat 0 holding `value` (created when absent; extra keys of an
 * existing track removed). Clears `importedLeadIn`. Returns the track id.
 */
export function setSingleKeyTrack(tx: Tx, line: LineId, prop: LineProp, layer: number, value: TrackValue): TrackId {
  const existing = tx.idx.track(line, prop, layer);
  if (existing === undefined) {
    return createTrack(tx, lineOwner(line, prop, layer), PROP_SPECS[prop].valueType, [
      { t: Beats.ZERO, vIn: value, vOut: value, ease: LINEAR },
    ]);
  }
  const keys = keysOf(tx, existing);
  const keep = keys.find((k) => Beats.eq(k.t, Beats.ZERO)) ?? keys[0];
  for (const k of keys) if (k !== keep) tx.remove('keys', k.id);
  const track = tx.doc.tracks.get(existing);
  if (track?.importedLeadIn === true) tx.update('tracks', existing, { importedLeadIn: undefined });
  if (keep === undefined) {
    const kid = tx.newId('keys');
    tx.insert('keys', { id: kid, track: existing, t: Beats.ZERO, vIn: value, vOut: value, ease: LINEAR, link: 0 });
  } else {
    tx.update('keys', keep.id, { t: Beats.ZERO, vIn: value, vOut: value, link: 0 });
  }
  return existing;
}

/** Ensures a line property has a track: when absent in every layer, one key at beat 0 with `value`. */
export function ensurePropTrack(tx: Tx, line: LineId, prop: LineProp, value: TrackValue): boolean {
  if (hasTrack(tx, line, prop)) return false;
  setSingleKeyTrack(tx, line, prop, 0, value);
  return true;
}

/**
 * Sets a key's leave value. Keeps the hold invariant: when the key's own segment is a hold, the next key's arrive
 * value follows (a hold always arrives as an explicit jump). A first key keeps vIn === vOut.
 */
export function setKeyOut(tx: Tx, key: Key, value: TrackValue): void {
  const prev = tx.idx.prevKey(key.id);
  if (prev === undefined) tx.update('keys', key.id, { vIn: value, vOut: value });
  else tx.update('keys', key.id, { vOut: value });
  if (key.ease.kind === 'hold') {
    const next = tx.idx.nextKey(key.id);
    if (next !== undefined) tx.update('keys', next, { vIn: value });
  }
}

/** Clears the link group of the segment starting at `key` and of the segment ending at it. Returns true if any. */
export function unlinkAround(tx: Tx, key: Key): boolean {
  let any = false;
  if (key.link !== 0) {
    tx.update('keys', key.id, { link: 0 });
    any = true;
  }
  const prevId = tx.idx.prevKey(key.id);
  const prev = prevId === undefined ? undefined : tx.doc.keys.get(prevId);
  if (prev !== undefined && prev.link !== 0) {
    tx.update('keys', prev.id, { link: 0 });
    any = true;
  }
  return any;
}

/**
 * Adds `delta` to a numeric key (arrive and leave values together, so the curve around it moves as a whole).
 * After a hold the arrive value stays (it is the held value). Linked neighbouring segments are unlinked first
 * (their partners would otherwise silently change too). Returns true when segments had to be unlinked.
 */
export function shiftKeyValue(tx: Tx, keyId: KeyId, delta: number): boolean {
  const before = tx.doc.keys.get(keyId);
  if (before === undefined || delta === 0) return false;
  const unlinked = unlinkAround(tx, before);
  const key = tx.doc.keys.get(keyId) as Key;
  const vOut = noNegZero((key.vOut as number) + delta);
  const vIn = noNegZero((key.vIn as number) + delta);
  const prevId = tx.idx.prevKey(keyId);
  const prev = prevId === undefined ? undefined : tx.doc.keys.get(prevId);
  if (prev === undefined) tx.update('keys', keyId, { vIn: vOut, vOut });
  else if (prev.ease.kind === 'hold') tx.update('keys', keyId, { vOut });
  else tx.update('keys', keyId, { vIn, vOut });
  if (key.ease.kind === 'hold') {
    const next = tx.idx.nextKey(keyId);
    if (next !== undefined) tx.update('keys', next, { vIn: vOut });
  }
  return unlinked;
}

/** A fresh link group id (larger than every link id in use). */
export function freshLinkId(tx: Tx, taken?: ReadonlySet<number>): number {
  let max = 0;
  for (const k of tx.doc.keys.values()) if (k.link > max) max = k.link;
  if (taken !== undefined) for (const l of taken) if (l > max) max = l;
  return max + 1;
}

// ---------------------------------------------------------------------------------------------------------------
// Removing a line's content
// ---------------------------------------------------------------------------------------------------------------

/** Deletes a line record with its notes and tracks; videos attached to it are detached. Order list: caller. */
export function deleteLineRecord(tx: Tx, line: LineId): void {
  for (const n of [...tx.idx.notesByLine(line)]) tx.remove('notes', n);
  for (const t of tracksOfLine(tx, line)) deleteTrack(tx, t);
  for (const v of [...tx.doc.videos.values()]) {
    if (v.attachLine === line) tx.update('videos', v.id, { attachLine: null });
  }
  tx.remove('lines', line);
}

/**
 * New order list. A full permutation replaces the order; a subset of the items is placed, in the given order, into
 * the slots those items occupy now (other items keep their places). Null when `order` has unknown or repeated ids.
 */
export function reorderedSubset<T>(current: readonly T[], order: readonly T[]): T[] | null {
  const present = new Set(current);
  const seen = new Set<T>();
  for (const id of order) {
    if (!present.has(id) || seen.has(id)) return null;
    seen.add(id);
  }
  let k = 0;
  return current.map((id) => (seen.has(id) ? (order[k++] as T) : id));
}

/** True when `ancestor` is `line` or one of its ancestors. */
export function isAncestorOrSelf(tx: Tx, ancestor: LineId, line: LineId): boolean {
  const limit = tx.doc.lines.size + 1;
  let cur: LineId | null = line;
  for (let i = 0; cur !== null && i <= limit; i++) {
    if (cur === ancestor) return true;
    cur = tx.doc.lines.get(cur)?.parent ?? null;
  }
  return false;
}

/** Every descendant of `line` (children first, breadth first). */
export function descendantsOf(tx: Tx, line: LineId): LineId[] {
  const out: LineId[] = [];
  const queue: LineId[] = [...tx.idx.childrenOf(line)];
  const seen = new Set<LineId>([line]);
  for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    queue.push(...tx.idx.childrenOf(id));
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Evaluation (model level, f64; DESIGN.md §6.2)
// ---------------------------------------------------------------------------------------------------------------

/**
 * Value of a numeric track at chart second `sec` (progress in seconds; before the first key the first segment is
 * extrapolated as Phira does for 'extrapolate' properties, otherwise the first value holds). Null for an empty track.
 */
export function numericTrackAt(tx: Tx, track: TrackId, sec: Sec): number | null {
  const keys = keysOf(tx, track);
  const first = keys[0];
  if (first === undefined) return null;
  const secs = keys.map((k) => tx.tempo.secAt(k.t));
  const t0 = secs[0] as number;
  const owner = tx.doc.tracks.get(track)?.owner;
  const extrapolate = owner?.kind === 'line' && PROP_SPECS[owner.prop].leadIn === 'extrapolate';
  if (sec < t0) {
    const second = keys[1];
    if (!extrapolate || second === undefined) return first.vOut as number;
    return segmentValue(first, second, t0, secs[1] as number, sec);
  }
  for (let i = keys.length - 1; i >= 0; i--) {
    const ti = secs[i] as number;
    if (sec < ti) continue;
    const k = keys[i] as Key;
    const next = keys[i + 1];
    if (next === undefined) return k.vOut as number;
    return segmentValue(k, next, ti, secs[i + 1] as number, sec);
  }
  return first.vOut as number;
}

function segmentValue(k: Key, next: Key, t0: Sec, t1: Sec, sec: Sec): number {
  const a = k.vOut as number;
  const b = next.vIn as number;
  if (t1 <= t0) return a;
  const y = EaseApi.modelY(k.ease, (sec - t0) / (t1 - t0));
  const v = a + (b - a) * y;
  return Number.isFinite(v) ? v : a;
}

/** Sum of a summed property over its layers at `sec` (absent = 0). */
export function propSumAt(tx: Tx, line: LineId, prop: 'moveX' | 'moveY' | 'rotate', sec: Sec): number {
  let sum = 0;
  for (const t of tracksOfProp(tx, line, prop)) sum += numericTrackAt(tx, t, sec) ?? 0;
  return sum;
}

/** Placement of a line in chart space: position (isotropic chart units) and rotation (degrees, CCW positive). */
export interface WorldPose {
  readonly x: number;
  readonly y: number;
  readonly rot: number;
}

const DEG = Math.PI / 180;

export function rotateVec(x: number, y: number, deg: number): readonly [number, number] {
  const c = Math.cos(deg * DEG);
  const s = Math.sin(deg * DEG);
  return [x * c - y * s, x * s + y * c];
}

/** Own offset of a line from its moves (rpe-format F§10.1): (Σ moveX / 675, Σ moveY / 450 / aspect). */
export function ownOffset(tx: Tx, line: LineId, sec: Sec): readonly [number, number] {
  const asp = tx.doc.pkg.aspectRatio;
  return [propSumAt(tx, line, 'moveX', sec) / 675, propSumAt(tx, line, 'moveY', sec) / 450 / asp];
}

/**
 * World pose of `line` at `sec` (F§10.1): rot = -Σrotate (+ parent's rot when rotateWithParent), pos = parent pos +
 * R(parent rot) · own offset. `parentOverride` evaluates the line as if its parent were that line (or none).
 */
export function worldPoseAt(tx: Tx, line: LineId, sec: Sec, parentOverride?: LineId | null): WorldPose {
  const chain: Line[] = [];
  const seen = new Set<LineId>();
  let cur: Line | undefined = tx.doc.lines.get(line);
  let first = true;
  while (cur !== undefined && !seen.has(cur.id)) {
    chain.push(cur);
    seen.add(cur.id);
    const parent: LineId | null = first && parentOverride !== undefined ? parentOverride : cur.parent;
    first = false;
    cur = parent === null ? undefined : tx.doc.lines.get(parent);
  }
  let pose: WorldPose | null = null;
  for (let i = chain.length - 1; i >= 0; i--) {
    const l = chain[i] as Line;
    const [ox, oy] = ownOffset(tx, l.id, sec);
    const ownRot = -propSumAt(tx, l.id, 'rotate', sec);
    if (pose === null) {
      pose = { x: ox, y: oy, rot: ownRot };
      continue;
    }
    const [rx, ry] = rotateVec(ox, oy, pose.rot);
    pose = { x: pose.x + rx, y: pose.y + ry, rot: ownRot + (l.rotateWithParent ? pose.rot : 0) };
  }
  return pose ?? { x: 0, y: 0, rot: 0 };
}

// ---------------------------------------------------------------------------------------------------------------
// Line defaults
// ---------------------------------------------------------------------------------------------------------------

/** Names of every line (for unique names). */
export function lineNames(tx: Tx): Set<string> {
  const out = new Set<string>();
  for (const l of tx.doc.lines.values()) out.add(l.name);
  return out;
}

/** A beat for messages ("12:3/4"). */
export function beatText(b: Beat): string {
  return formatBeat(b, 'colon');
}
